import { faCalendar, faVirus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card, Col, Input, Radio, Row, Typography, message, Button, Modal } from 'antd'
import dayjs from 'dayjs'
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DesktopMedicalHistoryNav from './Nav'
import DatePicker from '../../../Reusable Components/DatePicker'
import { updateCovidInfo } from '../../api/api'
import patMedHistReducer from '../../utils/reducer'
import '../style/DesktopCovidInfo.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const { Title, Text } = Typography

const DesktopCovidInfo = ({ clientForm, setFacet, setSaving }) => {
  const patMedHist = useSelector((state) => state.patMedHist.data) || {}
  const covidState = patMedHist.covidInfo || {
    hadCovid: false,
    fullyVaccinated: false,
    lastVaccinatedDate: null,
    updatedAt: new Date().toISOString()
  }
  const [covidInfo, setCovidInfo] = useState({
    hadCovid: covidState.hadCovid || false,
    fullyVaccinated: covidState.fullyVaccinated || false,
    lastVaccinatedDate: covidState.lastVaccinatedDate,
    updatedAt: covidState.updatedAt || new Date().toISOString()
  })

  const [showDatePickers, setShowDatePickers] = useState({
    vaccination: false
  })

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const initialState = useRef(null)

  // Capture initial state when component loads - with delay to ensure state is loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialState.current && covidInfo) {
        initialState.current = JSON.parse(JSON.stringify(covidInfo))
        console.log('Initial COVID info state captured:', initialState.current)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [covidInfo])

  // Clear initial state when component unmounts
  useEffect(() => {
    return () => {
      initialState.current = null
    }
  }, [])

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialState.current) return false

    const hasChanges = JSON.stringify(covidInfo) !== JSON.stringify(initialState.current)
    console.log('COVID info change detection:', {
      hasChanges,
      initial: initialState.current,
      current: covidInfo
    })

    return hasChanges
  }

  // Handle navigation with unsaved changes check
  const handleNavigation = (navigationFunction) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => navigationFunction)
      setShowUnsavedModal(true)
    } else {
      navigationFunction()
    }
  }

  // Update initial state after successful save
  const updateInitialStateAfterSave = () => {
    initialState.current = JSON.parse(JSON.stringify(covidInfo))
    console.log('Initial state updated after save:', initialState.current)
  }

  // Wrapper for handleSubmit that also updates initial state
  const handleSaveWithStateUpdate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle saving and then navigating
  const handleSaveAndNavigate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
      setShowUnsavedModal(false)
      if (pendingNavigation) {
        pendingNavigation()
        setPendingNavigation(null)
      }
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle navigation without saving
  const handleNavigateWithoutSaving = () => {
    setShowUnsavedModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const handleDateSelect = (field) => (date) => {
    const isoDate = date.toISOString()
    setCovidInfo((prev) => ({
      ...prev,
      [field]: isoDate
    }))
    setShowDatePickers((prev) => ({ ...prev, [field]: false }))
  }

  const updateField = (field, value) => {
    setCovidInfo((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const dispatch = useDispatch()
  const handleSubmit = async () => {
    // Check if we have a valid medical history record
    if (!patMedHist._id) {
      message.error('Medical history record not found. Please refresh the page and try again.')
      return
    }

    setSaving(true)
    try {
      const newRecord = await updateCovidInfo(patMedHist._id, covidInfo)
      dispatch(patMedHistReducer.actions.updateState(newRecord))
      setFacet(null)
    } catch (error) {
      console.error('Error saving COVID-19 information:', error)
      message.error('Failed to save COVID-19 information')
    }
    setSaving(false)
  }

  return (
    <div className='desktop-covid-info-container'>
      <DesktopSectionHeader
        icon={faVirus}
        title='Covid-19 Information'
        subtitle='Help us understand your COVID-19 vaccination status and health history'
        gradient='linear-gradient(135deg, #26A69A 0%, #26C6DA 100%)'
      />

      <div className='desktop-covid-info-content'>
        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
          {/* Summary Card */}
          <Col xs={24}>
            <Card className='desktop-covid-summary-card'>
              <Title level={3}>Summary</Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className='desktop-summary-item'>
                  <Text strong>COVID-19 Infection:</Text>
                  <Text className={covidInfo.hadCovid ? 'status-yes' : 'status-no'}>
                    {covidInfo.hadCovid ? 'Yes' : 'No'}
                  </Text>
                </div>
                <div className='desktop-summary-item'>
                  <Text strong>Vaccination Status:</Text>
                  <Text className={covidInfo.fullyVaccinated ? 'status-yes' : 'status-no'}>
                    {covidInfo.fullyVaccinated ? 'Fully Vaccinated' : 'Not Vaccinated'}
                  </Text>
                </div>
                {covidInfo.fullyVaccinated && covidInfo.lastVaccinatedDate && (
                  <div className='desktop-summary-item'>
                    <Text strong>Last Vaccination Date:</Text>
                    <Text>{dayjs(covidInfo.lastVaccinatedDate).format('DD MMMM YYYY')}</Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          {/* COVID Infection History */}
          <Col xs={24} lg={12}>
            <Card className='desktop-covid-section-card'>
              <div className='desktop-covid-section-header'>
                <FontAwesomeIcon icon={faVirus} className='desktop-covid-section-icon' />
                <Title level={4}>COVID-19 Infection History</Title>
              </div>

              <div className='desktop-covid-question'>
                <Text className='desktop-covid-question-text'>Have you ever had COVID-19?</Text>
                <Radio.Group
                  value={covidInfo.hadCovid}
                  onChange={(e) => updateField('hadCovid', e.target.value)}
                  className='desktop-covid-radio-group'
                >
                  <Radio value={true} className='desktop-covid-radio'>
                    Yes
                  </Radio>
                  <Radio value={false} className='desktop-covid-radio'>
                    No
                  </Radio>
                </Radio.Group>
              </div>
            </Card>
          </Col>

          {/* Vaccination Status */}
          <Col xs={24} lg={12}>
            <Card className='desktop-covid-section-card'>
              <div className='desktop-covid-section-header'>
                <FontAwesomeIcon icon={faVirus} className='desktop-covid-section-icon' />
                <Title level={4}>Vaccination Status</Title>
              </div>

              <div className='desktop-covid-question'>
                <Text className='desktop-covid-question-text'>Are you fully vaccinated against COVID-19?</Text>
                <Radio.Group
                  value={covidInfo.fullyVaccinated}
                  onChange={(e) => updateField('fullyVaccinated', e.target.value)}
                  className='desktop-covid-radio-group'
                >
                  <Radio value={true} className='desktop-covid-radio'>
                    Yes
                  </Radio>
                  <Radio value={false} className='desktop-covid-radio'>
                    No
                  </Radio>
                </Radio.Group>
              </div>

              {covidInfo.fullyVaccinated && (
                <div className='desktop-vaccination-date'>
                  <Text className='desktop-date-label'>Last Vaccination Date</Text>
                  <Button
                    block
                    className='desktop-date-input'
                    onClick={() => setShowDatePickers({ vaccination: true })}
                    icon={<FontAwesomeIcon icon={faCalendar} />}
                  >
                    {covidInfo.lastVaccinatedDate
                      ? dayjs(covidInfo.lastVaccinatedDate).format('DD MMMM YYYY')
                      : 'Select vaccination date'}
                  </Button>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>

      {/* Date Picker */}
      <DatePicker
        isVisible={showDatePickers.vaccination}
        onDateSelect={handleDateSelect('lastVaccinatedDate')}
        onCancel={() => setShowDatePickers({ vaccination: false })}
        maximumDate={new Date()}
      />

      <DesktopMedicalHistoryNav
        prevFunction={() => handleNavigation(() => setFacet(null))}
        nextFunction={handleSaveWithStateUpdate}
        prevText='Back to Overview'
        nextText='Save COVID Information'
      />

      {/* Unsaved Changes Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faVirus} style={{ color: '#ff6b6b' }} />
            <span>Unsaved Changes</span>
          </div>
        }
        open={showUnsavedModal}
        onCancel={() => setShowUnsavedModal(false)}
        footer={null}
        className='desktop-unsaved-changes-modal'
        centered
      >
        <div className='desktop-unsaved-changes-content'>
          <Typography.Text className='desktop-unsaved-changes-message'>
            You have unsaved changes to your COVID information. What would you like to do?
          </Typography.Text>

          <div className='desktop-unsaved-changes-actions'>
            <Button type='primary' className='desktop-save-changes-btn' onClick={handleSaveAndNavigate} loading={false}>
              Save Changes
            </Button>
            <Button type='default' className='desktop-discard-changes-btn' onClick={handleNavigateWithoutSaving}>
              Discard Changes
            </Button>
            <Button type='text' className='desktop-cancel-modal-btn' onClick={() => setShowUnsavedModal(false)}>
              Continue Editing
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default DesktopCovidInfo
